import { store } from '../state/store';

export type DrivingResponseSource = 'undecided' | 'raw' | 'driving';

export class DrivingResponseTracker {
  private currentAssistantDrivingAccumulated = '';
  private currentAssistantDrivingSource: DrivingResponseSource = 'undecided';

  extractDrivingResponseText(parsed: Record<string, unknown>): string {
    const keys = [
      'drivingText',
      'driving_text',
      'ttsText',
      'tts_text',
      'voiceText',
      'voice_text',
      'spokenText',
      'spoken_text',
    ];

    const data = parsed.data && typeof parsed.data === 'object'
      ? parsed.data as Record<string, unknown>
      : undefined;
    const delta = parsed.delta && typeof parsed.delta === 'object'
      ? parsed.delta as Record<string, unknown>
      : undefined;

    return this.readStringField(data, keys)
      || this.readStringField(delta, keys)
      || this.readStringField(parsed, keys);
  }

  reset(): void {
    this.currentAssistantDrivingAccumulated = '';
    this.currentAssistantDrivingSource = 'undecided';
  }

  updateLastResponseFromRaw(accumulated: string): void {
    if (!store.getState().drivingMode.active) return;
    if (this.ensureDrivingResponseSource('raw') !== 'raw') return;
    store.setDrivingMode({ lastResponse: accumulated });
  }

  updateLastResponseFromDriving(incoming: string): void {
    if (!store.getState().drivingMode.active) return;
    this.mergeDrivingText(incoming);
    if (this.ensureDrivingResponseSource('driving') !== 'driving') return;
    store.setDrivingMode({ lastResponse: this.currentAssistantDrivingAccumulated });
  }

  finalizeLastResponse(accumulated: string): void {
    if (!store.getState().drivingMode.active) return;
    store.setDrivingMode({
      lastResponse: this.getDrivingResponseText(accumulated),
      processing: false,
    });
  }

  private readStringField(
    source: Record<string, unknown> | undefined,
    keys: string[],
  ): string {
    if (!source) return '';
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
    return '';
  }

  private mergeDrivingResponseText(previous: string, incoming: string): string {
    if (!incoming) return previous;
    if (!previous) return incoming;
    if (incoming.startsWith(previous)) return incoming;
    if (previous.endsWith(incoming)) return previous;
    return previous + incoming;
  }

  private getDrivingResponseText(fallback = ''): string {
    if (this.currentAssistantDrivingSource === 'driving') {
      return this.currentAssistantDrivingAccumulated;
    }
    if (this.currentAssistantDrivingSource === 'raw') {
      return fallback;
    }
    return '';
  }

  private ensureDrivingResponseSource(preferred: Exclude<DrivingResponseSource, 'undecided'>): DrivingResponseSource {
    if (this.currentAssistantDrivingSource === 'undecided') {
      this.currentAssistantDrivingSource = preferred;
    }
    return this.currentAssistantDrivingSource;
  }

  private mergeDrivingText(incoming: string): void {
    if (!incoming) return;
    this.currentAssistantDrivingAccumulated = this.mergeDrivingResponseText(
      this.currentAssistantDrivingAccumulated,
      incoming,
    );
  }
}
